import {
  parseMollieCreditcard,
} from 'components/ordering/checkout/payment-methods/methods/mollie/credit-card/mollie-credit-card-parser.js'
import {
  parseMollieGeneric,
} from 'components/ordering/checkout/payment-methods/methods/mollie/generic/mollie-generic-parser.js'
import {
  parseMollieIn3,
} from 'components/ordering/checkout/payment-methods/methods/mollie/in3/mollie-in3-parser.js'
import {
  parseMollieRiverty,
} from 'components/ordering/checkout/payment-methods/methods/mollie/riverty/mollie-riverty-parser.js'
import {
  parseMollieKlarna,
} from 'components/ordering/checkout/payment-methods/methods/mollie/klarnapaylater/mollie-klarna-parser.js'
import { parseOnAccount } from 'components/ordering/checkout/payment-methods/methods/on-account/on-account-parser.js'
import { parseOverrulePaymentMethod } from 'components/ordering/checkout/payment-methods/methods/overrule/overrule-parser.js'
import { CSCPaymentMethod } from 'components/ordering/checkout/payment-methods/methods/payment-method.js'
import { CouldNotParse } from 'components/ordering/checkout/payment-methods/payment-backend/could-not-parse.js'
import { parseMollieApplePay } from 'components/ordering/checkout/payment-methods/methods/mollie/apple-pay/mollie-apple-pay-parser.js'


type ApiUrls = {
  selectPaymentMethodUrl: URL
  startPaymentUrl: URL
}

type MollieConfigDto = {
  mollie: unknown
}

const hasMollieConfig = (response: unknown): response is MollieConfigDto => {
  return (response as MollieConfigDto)?.mollie !== undefined
}

type OnAccountConfigDto = {
  on_account: unknown
}

const hasOnAccountConfig = (response: unknown): response is OnAccountConfigDto => {
  return (response as OnAccountConfigDto)?.on_account !== undefined
}

type OverruleMethodsConfigDto = {
  overrule_methods: unknown[]
}

const hasOverruleMethodsConfig = (response: unknown): response is OverruleMethodsConfigDto => {
  return Array.isArray((response as OverruleMethodsConfigDto)?.overrule_methods)
}

type SelectedPaymentMethodDto = {
  selected_payment_method: string
}

const hasSelectedPaymentMethod = (response: unknown): response is SelectedPaymentMethodDto => {
  return typeof (response as SelectedPaymentMethodDto)?.selected_payment_method === 'string'
}

type ApiPathsDto = {
  select_payment_method_path: string
  start_payment_path: string
}

const hasApiUrls = (config: unknown): config is ApiPathsDto => {
  return (
    typeof (config as ApiPathsDto)?.select_payment_method_path === 'string'
    && typeof (config as ApiPathsDto)?.start_payment_path === 'string'
  )
}

type MollieMethodsDto = {
  methods: Record<string, unknown>
  method_sorting: string[]
}

const hasMollieMethods = (config: unknown): config is MollieMethodsDto => {
  return (
    typeof (config as MollieMethodsDto)?.methods === 'object'
    && Array.isArray((config as MollieMethodsDto)?.method_sorting)
    && (config as MollieMethodsDto).method_sorting.every((method: unknown) => typeof method === 'string')
  )
}

const isDefined = <T>(val: T | undefined | void): val is T => {
  return val !== undefined
}

export const parseMollieApiUrls = (response: unknown): ApiUrls | typeof CouldNotParse => {
  if (!hasMollieConfig(response) || !hasApiUrls(response.mollie)) { return CouldNotParse }
  return parseApiUrls(response.mollie)
}

export const parseOnAccountApiUrls = (response: unknown): ApiUrls | typeof CouldNotParse => {
  if (!hasOnAccountConfig(response) || !hasApiUrls(response.on_account)) { return CouldNotParse }
  return parseApiUrls(response.on_account)
}

const parseApiUrls = (apiPathsDto: ApiPathsDto): ApiUrls => ({
  selectPaymentMethodUrl: new URL(apiPathsDto.select_payment_method_path, window.location.origin),
  startPaymentUrl: new URL(apiPathsDto.start_payment_path, window.location.origin),
})

const typiaParser = (molliePaymentMethods: Record<string, unknown>, selectedPaymentMethod: string): Record<string, CSCPaymentMethod | undefined> => {
  const parsers = [
    parseMollieCreditcard,
    parseMollieKlarna,
    parseMollieIn3,
    parseMollieRiverty,
    parseMollieApplePay,
    parseMollieGeneric,
  ]

  const entries = Object.entries(molliePaymentMethods).map(([key, paymentMethod]) => {
    for (const parser of parsers) {
      const result = parser(paymentMethod, selectedPaymentMethod)
      if (result != CouldNotParse) {
        return [key, result]
      }
    }
    console.warn(
      `[payment-methods] Could not parse payment method "${key}". `
      + 'The backend returned this method but no parser could handle it. '
      + 'Check that the backend DTO fields match the TypeScript interface.',
      paymentMethod,
    )
    return [key, undefined]
  })

  return Object.fromEntries(entries)
}

export const parseMolliePaymentMethods = (response: unknown): CSCPaymentMethod[] | typeof CouldNotParse => {
  if (!hasMollieConfig(response)) { return CouldNotParse }
  if (!hasMollieMethods(response.mollie)) { throw new Error('Mollie config found, but no configured payment methods.') }
  if (!hasSelectedPaymentMethod(response)) { throw new Error('No selected payment method found.') }

  const selectedPaymentMethod = response.selected_payment_method
  const methodsById = typiaParser(response.mollie.methods, selectedPaymentMethod)

  const thingies = response.mollie.method_sorting
    .map((methodId: string) => methodsById[methodId])
    .filter(isDefined)

  return thingies
}

export const parseOnAccountPaymentMethod = (response: unknown): CSCPaymentMethod | typeof CouldNotParse => {
  if (!hasOnAccountConfig(response)) { return CouldNotParse }
  if (!hasSelectedPaymentMethod(response)) { throw new Error('No selected payment method found.') }

  const selectedPaymentMethod = response.selected_payment_method
  return parseOnAccount(response.on_account, selectedPaymentMethod)
}

type OverruleApiPathsConfigDto = {
  overrule: unknown
}

const hasOverruleApiPathsConfig = (response: unknown): response is OverruleApiPathsConfigDto => {
  return (response as OverruleApiPathsConfigDto)?.overrule !== undefined
}

export const parseOverruleApiUrls = (response: unknown): ApiUrls | typeof CouldNotParse => {
  if (!hasOverruleApiPathsConfig(response) || !hasApiUrls(response.overrule)) { return CouldNotParse }
  return parseApiUrls(response.overrule)
}

export const parseOverrulePaymentMethods = (response: unknown): CSCPaymentMethod[] => {
  if (!hasOverruleMethodsConfig(response)) { return [] }
  if (!hasSelectedPaymentMethod(response)) { throw new Error('No selected payment method found.') }

  const selectedPaymentMethod = response.selected_payment_method
  const parsedMethods = response.overrule_methods
    .map(method => parseOverrulePaymentMethod(method, selectedPaymentMethod))

  return parsedMethods.filter(
    (method): method is Exclude<typeof method, typeof CouldNotParse> => method !== CouldNotParse
  )
}
